import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, User, ShoppingBag, Tag, ShieldCheck, Clock, MailCheck, Eye, CalendarClock, AlertTriangle, CheckCircle2, XCircle, PackageCheck, Pencil, Store, Save, X, Trash2 } from "lucide-react";
import { GlassCard, StatusBadge } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { formatGBP } from "../../components/common/StockBadge";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const MANAGER_ROLES = ["super_admin", "manager"];
const isValidUkPhone = (v) => /^(?:\+44|0)\d{9,10}$/.test(String(v || "").replace(/[\s()-]/g, ""));

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Reservation Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection", COLLECTED: "Collected", CANCELLED: "Cancelled",
};
const LEGAL_LABELS = {
  age_confirmed: "18 or over confirmed", terms_accepted: "Terms accepted",
  privacy_ack: "Privacy acknowledged", challenge25_ack: "Challenge 25 acknowledged",
};
const CANCEL_REASON_LABELS = {
  customer_requested: "Customer requested", out_of_stock: "Out of stock", did_not_collect: "Did not collect",
  duplicate: "Duplicate reservation", unable_to_contact: "Unable to contact", age_id_issue: "Age/ID issue", other: "Other",
};
const fmt = (d) => (d ? String(d).replace("T", " ").slice(0, 16) : "—");
const daysLabel = (n) => n == null ? "" : n < 0 ? "Collection date passed" : n === 0 ? "Collection today" : n === 1 ? "Collection tomorrow" : `${n} days until collection`;
const GATE_REASON = {
  INVENTORY_UNVERIFIED: "Inventory not verified", INSUFFICIENT_STOCK: "Not enough stock",
  PRODUCT_INACTIVE: "Product inactive", PRODUCT_NOT_FOUND: "Product not found",
  COMING_SOON: "Coming soon", INVALID_QUANTITY: "Invalid quantity",
  INSUFFICIENT_RESERVED: "Reserved stock mismatch", INSUFFICIENT_ON_HAND: "On-hand stock too low",
};

export default function AdminReservationDetail() {
  const { number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = MANAGER_ROLES.includes(user?.role);
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);   // { action, label }
  const [cancelReason, setCancelReason] = useState("customer_requested");
  const [cancelNote, setCancelNote] = useState("");
  const [gateError, setGateError] = useState(null);   // failing items from a rejected confirm
  const [collectAck, setCollectAck] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [stores, setStores] = useState([]);
  const [edit, setEdit] = useState(null);

  const reload = () => api.get(`/admin/reservations/${number}`).then(({ data }) => setR(data)).catch(() => setR(false)).finally(() => setLoading(false));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [number]);

  const startEdit = async () => {
    try {
      const { data } = await api.get("/checkout/stores");
      setStores(data?.stores || []);
    } catch { setStores([]); }
    setEdit({
      first_name: r.contact?.first_name || "", last_name: r.contact?.last_name || "",
      phone: r.contact?.phone || "", store_id: r.store_id || "",
      collection_date: r.collection_date || "",
      items: (r.items || []).map((i) => ({ product_id: i.product_id, name: i.name, unit_price: i.unit_price, qty: i.qty })),
      adjustment_amount: r.admin_adjustment?.amount ? String(r.admin_adjustment.amount) : "",
      adjustment_reason: r.admin_adjustment?.reason || "",
    });
    setEditing(true);
  };

  const editSubtotal = (edit?.items || []).reduce((s, i) => s + (i.unit_price || 0) * (i.qty || 0), 0);
  const editAdj = parseFloat(edit?.adjustment_amount || "0") || 0;
  const editDiscountTotal = r?.discount_total || 0;
  const editTotal = Math.max(0, editSubtotal - editDiscountTotal - editAdj);
  const editPhoneValid = isValidUkPhone(edit?.phone || "");

  const setItemQty = (pid, qty) => setEdit((e) => ({ ...e, items: e.items.map((i) => i.product_id === pid ? { ...i, qty: Math.max(1, qty) } : i) }));
  const removeItem = (pid) => setEdit((e) => ({ ...e, items: e.items.filter((i) => i.product_id !== pid) }));

  const saveEdit = async () => {
    if (!editPhoneValid) { toast.error("Please enter a valid phone number."); return; }
    if ((edit.items || []).length === 0) { toast.error("Keep at least one item on the reservation."); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/admin/reservations/${number}`, {
        contact: { phone: edit.phone.trim() },
        store_id: edit.store_id,
        collection_date: edit.collection_date,
        items: edit.items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        admin_adjustment: { amount: editAdj, reason: edit.adjustment_reason.trim() },
      });
      setR(data); setEditing(false); setEdit(null);
      toast.success("Reservation updated — an updated confirmation email was sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes.");
    } finally { setSavingEdit(false); }
  };

  const openDialog = (a) => { setGateError(null); setCollectAck(false); setDialog(a); };

  const runAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/reservations/${number}/transition`, { action, ...extra });
      setR(data); setDialog(null); setCancelNote(""); setGateError(null);
      toast.success("Reservation updated.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if ((d?.code === "inventory_gate_failed" || d?.code === "collection_gate_failed") && Array.isArray(d.items)) {
        setGateError(d.items);
        toast.error(d.code === "collection_gate_failed" ? "Collection check failed — nothing was collected." : "Inventory check failed — no stock was committed.");
        reload();
      } else {
        toast.error(typeof d === "string" ? d : d?.message || "Could not update reservation.");
      }
    } finally { setBusy(false); }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (!r) return <div className="text-white/60" data-testid="admin-res-not-found">Reservation not found.</div>;

  const legal = r.legal || {};
  const acc = r.customer_account || {};
  const actions = r.available_actions || [];
  const ro = r.review_owner;
  const inv = r.inventory_check || {};
  const isConfirm = dialog?.action === "confirm";
  const isReady = dialog?.action === "ready";
  const isCollected = dialog?.action === "collected";
  const isCancel = dialog?.action === "cancel";
  const isConfirmedCancel = isCancel && (r.status === "CONFIRMED" || r.status === "READY_FOR_COLLECTION");
  const canConfirm = inv.can_confirm !== false;
  const itemCount = (r.items || []).reduce((s, i) => s + (i.qty || 1), 0);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-reservation-detail">
      <button onClick={() => navigate("/admin/reservations")} className="text-white/60 hover:text-white flex items-center gap-2 text-sm"><ArrowLeft className="h-4 w-4" /> Back to reservations</button>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-gold">{r.reservation_number}</h1>
          <p className="mt-1 text-xs text-white/60">Created {fmt(r.created_at)} · {r.collection_store?.name || `Store ${r.store_id}`}</p>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && r.status !== "CANCELLED" && r.status !== "COLLECTED" && !editing && (
            <Button data-testid="edit-reservation-btn" onClick={startEdit} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 gap-2">
              <Pencil className="h-4 w-4" /> Edit reservation
            </Button>
          )}
          <StatusBadge status={STATUS_LABEL[r.status] || "Received"} />
        </div>
      </div>

      {/* Days until collection */}
      <div className="flex items-center gap-2 text-sm text-white/80" data-testid="admin-days-until">
        <CalendarClock className="h-4 w-4 text-gold" /> {daysLabel(r.days_until_collection)} · {r.collection_date}
      </div>

      {/* Edit panel */}
      {editing && edit && (
        <GlassCard className="p-5 border-gold/30" hover={false} data-testid="edit-reservation-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-playfair text-lg text-white flex items-center gap-2"><Pencil className="h-5 w-5 text-gold" /> Edit reservation</h2>
            <button onClick={() => { setEditing(false); setEdit(null); }} className="text-white/60 hover:text-white" data-testid="edit-cancel-x"><X className="h-5 w-5" /></button>
          </div>

          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-white/80">First name</Label>
              <div data-testid="edit-first-name-readonly" className="mt-1 flex items-center h-10 px-3 rounded-md bg-black/20 border border-white/10 text-white/60 text-sm">{edit.first_name || "—"}</div>
              <p className="mt-1 text-[11px] text-white/45">From customer account (not editable here)</p>
            </div>
            <div>
              <Label className="text-white/80">Last name</Label>
              <div data-testid="edit-last-name-readonly" className="mt-1 flex items-center h-10 px-3 rounded-md bg-black/20 border border-white/10 text-white/60 text-sm">{edit.last_name || "—"}</div>
              <p className="mt-1 text-[11px] text-white/45">From customer account (not editable here)</p>
            </div>
            <div>
              <Label className="text-white/80">Phone</Label>
              <Input data-testid="edit-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className={`mt-1 bg-black/30 text-white h-10 ${edit.phone && !editPhoneValid ? "border-firered" : "border-white/15"}`} />
              {edit.phone && !editPhoneValid && <p className="mt-1 text-xs text-firered" data-testid="edit-phone-error">Please enter a valid phone number.</p>}
            </div>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">Collection store</Label>
              <select data-testid="edit-store-select" value={edit.store_id} onChange={(e) => setEdit({ ...edit, store_id: e.target.value })}
                className="mt-1 w-full bg-black/30 border border-white/15 text-white rounded-md px-3 h-10 text-sm">
                {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.name}{s.town ? ` — ${s.town}` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-white/80">Collection date</Label>
              <Input data-testid="edit-collection-date" type="date" value={edit.collection_date} onChange={(e) => setEdit({ ...edit, collection_date: e.target.value })} className="mt-1 bg-black/30 border-white/15 text-white h-10" />
            </div>
          </div>

          {/* Items (qty / remove) */}
          <div className="mt-5">
            <Label className="text-white/80">Items</Label>
            <div className="mt-2 space-y-2" data-testid="edit-items">
              {edit.items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid={`edit-item-${it.product_id}`}>
                  <span className="flex-1 min-w-0 truncate text-sm text-white/90">{it.name} <span className="text-white/50">@ {formatGBP(it.unit_price)}</span></span>
                  <Input type="number" min={1} value={it.qty} onChange={(e) => setItemQty(it.product_id, parseInt(e.target.value || "1", 10))} data-testid={`edit-item-qty-${it.product_id}`} className="w-20 bg-black/30 border-white/15 text-white h-9" />
                  <button onClick={() => removeItem(it.product_id)} className="text-firered hover:text-firered/80" data-testid={`edit-item-remove-${it.product_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Manual discount */}
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80">Goodwill discount (£)</Label>
              <Input data-testid="edit-adjustment-amount" type="number" min={0} step="0.01" value={edit.adjustment_amount} onChange={(e) => setEdit({ ...edit, adjustment_amount: e.target.value })} placeholder="0.00" className="mt-1 bg-black/30 border-white/15 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80">Discount reason</Label>
              <Input data-testid="edit-adjustment-reason" value={edit.adjustment_reason} onChange={(e) => setEdit({ ...edit, adjustment_reason: e.target.value })} placeholder="e.g. compensation for unavailable item" className="mt-1 bg-black/30 border-white/15 text-white h-10" />
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-white/10 bg-black/20 p-3 text-sm space-y-1">
            <div className="flex justify-between text-white/70"><span>Subtotal</span><span>{formatGBP(editSubtotal)}</span></div>
            {editDiscountTotal > 0 && <div className="flex justify-between text-emerald-400"><span>Existing discounts</span><span>− {formatGBP(editDiscountTotal)}</span></div>}
            {editAdj > 0 && <div className="flex justify-between text-emerald-400"><span>Goodwill discount</span><span>− {formatGBP(editAdj)}</span></div>}
            <div className="flex justify-between text-white font-semibold pt-1 border-t border-white/10"><span>New estimated total</span><span data-testid="edit-new-total">{formatGBP(editTotal)}</span></div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button data-testid="edit-save-btn" onClick={saveEdit} disabled={savingEdit} className="cf-gold-btn font-semibold gap-2">
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save & email customer</>}
            </Button>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => { setEditing(false); setEdit(null); }}>Cancel</Button>
          </div>
        </GlassCard>
      )}

      {/* Review-owner banner */}
      {ro && r.status === "UNDER_REVIEW" && (
        <div className={`rounded-xl border p-3 flex items-center gap-2 text-sm ${ro.is_me ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber/40 bg-amber/10 text-amber"}`} data-testid="review-owner-banner">
          <Eye className="h-4 w-4" /> {ro.is_me ? "You are reviewing this reservation" : `Under review by ${ro.name}`}{ro.started_at ? ` · since ${fmt(ro.started_at)}` : ""}
        </div>
      )}

      {/* Action panel */}
      {actions.length > 0 && (
        <GlassCard className="p-5" hover={false} data-testid="admin-action-panel">
          <h2 className="font-playfair text-lg text-white">Actions</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {actions.map((a) => (
              <div key={a.action} className="flex flex-col">
                <Button
                  data-testid={`action-${a.action}`}
                  disabled={!a.enabled || busy}
                  onClick={() => openDialog(a)}
                  variant={a.action === "cancel" ? "outline" : "default"}
                  className={a.action === "cancel" ? "border-firered/50 text-firered hover:bg-firered/10" : "cf-gold-btn font-semibold"}>
                  {a.label}
                </Button>
                {a.gated_reason && <span className="mt-1 text-[10px] text-white/40 max-w-[160px]">{a.gated_reason}</span>}
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Confirmation / cancel dialog */}
      <AlertDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="bg-navy border-white/15" data-testid="action-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              {dialog?.action === "cancel" && <AlertTriangle className="h-5 w-5 text-firered" />}{dialog?.label}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {isCancel
                ? "Please provide a reason for cancelling this reservation."
                : isConfirm
                  ? `Review inventory before confirming reservation ${r.reservation_number}.`
                  : isReady
                    ? `Prepare reservation ${r.reservation_number} for collection.`
                    : isCollected
                      ? `Complete the in-store collection for reservation ${r.reservation_number}.`
                      : `Confirm "${dialog?.label}" for reservation ${r.reservation_number}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Shared reservation summary for ready/collected */}
          {(isReady || isCollected) && (
            <div className="space-y-3" data-testid={isReady ? "ready-panel" : "collected-panel"}>
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm text-white/70 space-y-1">
                <div className="flex justify-between"><span>Customer</span><span className="text-white">{r.contact?.first_name} {r.contact?.last_name}</span></div>
                <div className="flex justify-between"><span>Collection date</span><span className="text-white">{r.collection_date}</span></div>
                <div className="flex justify-between"><span>Items</span><span className="text-white">{itemCount}</span></div>
                <div className="flex justify-between"><span>Estimated total in store</span><span className="text-white">{formatGBP(r.estimated_total)}</span></div>
              </div>
              <div className="rounded-lg border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" /> Challenge 25 — remind the customer to bring valid photo ID on collection.
              </div>
              {isReady && (
                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70" data-testid="ready-no-stock-note">
                  This confirms the reserved items have been prepared and are ready for collection. No inventory quantities will change at this stage.
                </div>
              )}
              {isCollected && (
                <>
                  {gateError && (
                    <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
                      {gateError.map((it) => (
                        <div key={it.product_id} className="flex items-center gap-2 rounded-md px-2.5 py-2 text-xs bg-firered/10 text-red-200">
                          <XCircle className="h-4 w-4 shrink-0 text-firered" />
                          <span className="flex-1 min-w-0 truncate">{it.name} <span className="text-white/40">({it.sku || "—"})</span></span>
                          <span className="shrink-0 font-medium">{GATE_REASON[it.reason] || it.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="rounded-lg border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber flex items-start gap-2" data-testid="collected-stock-warning">
                    <PackageCheck className="h-4 w-4 mt-0.5 shrink-0" /> Marking as collected will reduce on-hand stock (goods leaving the shop). No online payment is taken — the sale is completed in store.
                  </div>
                  <label className="flex items-start gap-2 text-xs text-white/80 cursor-pointer" data-testid="collected-ack-label">
                    <input type="checkbox" data-testid="collected-ack" checked={collectAck} onChange={(e) => setCollectAck(e.target.checked)} className="mt-0.5" />
                    I confirm the customer has collected this reservation and the in-store sale has been completed.
                  </label>
                </>
              )}
            </div>
          )}

          {/* Confirm: inventory validation summary */}
          {isConfirm && (
            <div className="space-y-3" data-testid="confirm-inventory-panel">
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm text-white/70 space-y-1">
                <div className="flex justify-between"><span>Customer</span><span className="text-white">{r.contact?.first_name} {r.contact?.last_name}</span></div>
                <div className="flex justify-between"><span>Collection date</span><span className="text-white">{r.collection_date}</span></div>
                <div className="flex justify-between"><span>Items</span><span className="text-white">{(r.items || []).reduce((s, i) => s + (i.qty || 1), 0)}</span></div>
                <div className="flex justify-between"><span>Estimated total in store</span><span className="text-white">{formatGBP(r.estimated_total)}</span></div>
              </div>
              <div className="space-y-1.5 max-h-52 overflow-auto pr-1">
                {(gateError || inv.items || []).map((it) => (
                  <div key={it.product_id} data-testid={`inv-check-${it.product_id}`}
                    className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-xs ${it.ok ? "bg-emerald-500/10 text-emerald-200" : "bg-firered/10 text-red-200"}`}>
                    {it.ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> : <XCircle className="h-4 w-4 shrink-0 text-firered" />}
                    <span className="flex-1 min-w-0 truncate">{it.name} <span className="text-white/40">({it.sku || "—"})</span></span>
                    <span className="shrink-0">req {it.requested}{it.available != null ? ` · avail ${it.available}` : ""}</span>
                    {!it.ok && <span className="shrink-0 font-medium">{GATE_REASON[it.reason] || it.reason}</span>}
                  </div>
                ))}
              </div>
              {canConfirm && !gateError ? (
                <div className="rounded-lg border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber flex items-start gap-2" data-testid="confirm-stock-warning">
                  <PackageCheck className="h-4 w-4 mt-0.5 shrink-0" /> Confirming this reservation will reserve the required stock and reduce availability.
                </div>
              ) : (
                <div className="rounded-lg border border-firered/40 bg-firered/10 px-3 py-2 text-xs text-red-200 flex items-start gap-2" data-testid="confirm-blocked-warning">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> One or more items cannot be confirmed. Resolve the inventory issues above before confirming.
                </div>
              )}
            </div>
          )}

          {isCancel && (
            <div className="space-y-3">
              {isConfirmedCancel && (
                <div className="rounded-lg border border-amber/40 bg-amber/10 px-3 py-2 text-xs text-amber flex items-start gap-2" data-testid="cancel-restore-warning">
                  <PackageCheck className="h-4 w-4 mt-0.5 shrink-0" /> Cancelling this reservation will release the reserved stock back to available inventory.
                </div>
              )}
              <select data-testid="cancel-reason-select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm">
                {Object.entries(CANCEL_REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {cancelReason === "other" && (
                <textarea data-testid="cancel-note" value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Add a note…"
                  className="w-full bg-black/30 border border-white/15 text-white rounded-md px-3 py-2 text-sm min-h-[70px]" />
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 text-white bg-transparent">Back</AlertDialogCancel>
            <AlertDialogAction
              data-testid="action-confirm-btn"
              disabled={busy || (isConfirm && (!canConfirm || !!gateError)) || (isCollected && !collectAck)}
              onClick={(e) => { e.preventDefault(); isCancel ? runAction("cancel", { reason: cancelReason, note: cancelNote }) : runAction(dialog.action); }}
              className={isCancel ? "bg-firered hover:bg-firered/90 text-white" : "cf-gold-btn font-semibold"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : isConfirm ? "Confirm & Reserve Stock"
                : isReady ? "Mark Ready for Collection"
                : isCollected ? "Mark Collected"
                : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Customer */}
        <GlassCard className="p-6" hover={false} data-testid="admin-res-customer">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><User className="h-5 w-5 text-gold" /> Customer</h2>
          <div className="mt-3 text-sm text-white/70 space-y-1">
            <p>{r.contact?.first_name} {r.contact?.last_name}</p>
            <p className="flex items-center gap-2">{r.contact?.email}{acc.is_verified && <span className="text-emerald-400 text-xs">· Verified</span>}</p>
            <p>{r.contact?.phone}</p>
            <p className="text-white/60 text-xs pt-1">Account {acc.user_id} · via {acc.auth_provider} · Newsletter {acc.marketing_consent ? "on" : "off"}</p>
          </div>
        </GlassCard>

        {/* Collection */}
        <GlassCard className="p-6" hover={false} data-testid="admin-res-collection">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><Store className="h-5 w-5 text-gold" /> Collection store</h2>
          <div className="mt-3 text-sm space-y-1">
            <p className="text-white font-semibold text-base" data-testid="admin-res-store-name">{r.collection_store?.name || r.collection_location_label}</p>
            {r.collection_store?.full_address && <p className="text-white/80">{r.collection_store.full_address}</p>}
            {r.collection_store?.phone && <p className="text-white/70">Tel: {r.collection_store.phone}</p>}
            {r.collection_store?.collection_instructions && (
              <p className="text-gold/90 pt-1" data-testid="admin-res-store-instructions">{r.collection_store.collection_instructions}</p>
            )}
            <p className="text-white/80 pt-2 border-t border-white/10 mt-2">Preferred date: <strong className="text-white">{r.collection_date}</strong></p>
          </div>
        </GlassCard>
      </div>

      {/* Items */}
      <GlassCard className="p-6" hover={false} data-testid="admin-res-items">
        <h2 className="font-playfair text-lg text-white flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-gold" /> Items</h2>
        <div className="mt-4 space-y-3">
          {(r.items || []).map((it) => (
            <div key={it.product_id} className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg overflow-hidden bg-black/30 shrink-0">
                {it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-white/20"><ShoppingBag className="h-5 w-5" /></div>}
              </div>
              <div className="flex-1 min-w-0"><p className="text-white text-sm truncate">{it.name}</p><p className="text-white/40 text-xs">{it.sku || "—"} · {it.qty} × {formatGBP(it.unit_price)}</p></div>
              <p className="text-white text-sm">{formatGBP(it.line_total)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-white/10 pt-3 space-y-1 text-sm">
          <div className="flex justify-between text-white/70"><span>Subtotal</span><span>{formatGBP(r.subtotal)}</span></div>
          {(r.discounts || []).map((d) => (
            <div key={d.promotion_id} className="flex justify-between text-emerald-400"><span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{d.label}{d.is_voucher ? " (coupon)" : ""}</span><span>− {formatGBP(d.amount)}</span></div>
          ))}
          {r.voucher_code && <div className="flex justify-between text-white/50"><span>Coupon code</span><span>{r.voucher_code}</span></div>}
          {r.admin_adjustment?.amount > 0 && (
            <div className="flex justify-between text-emerald-400" data-testid="admin-res-adjustment">
              <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />Goodwill discount{r.admin_adjustment.reason ? ` (${r.admin_adjustment.reason})` : ""}</span>
              <span>− {formatGBP(r.admin_adjustment.amount)}</span>
            </div>
          )}
          <div className="flex justify-between text-white font-semibold pt-2 border-t border-white/10"><span>Estimated total payable in store</span><span data-testid="admin-res-total">{formatGBP(r.estimated_total)}</span></div>
        </div>
      </GlassCard>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Timeline */}
        <GlassCard className="p-6" hover={false} data-testid="admin-res-timeline">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><Clock className="h-5 w-5 text-gold" /> Timeline</h2>
          <ol className="mt-4 space-y-3 border-l border-white/10 pl-4">
            {(r.timeline || []).map((t, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-gold" />
                <p className="text-sm text-white">{STATUS_LABEL[t.status] || t.status}</p>
                <p className="text-xs text-white/60">{fmt(t.at)}{t.note ? ` · ${t.note}` : ""}</p>
              </li>
            ))}
          </ol>
        </GlassCard>

        {/* Legal + system */}
        <GlassCard className="p-6" hover={false} data-testid="admin-res-legal">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" /> Legal & system</h2>
          <div className="mt-3 space-y-1.5 text-sm text-white/70">
            {[["age_confirmed", "age_confirmed_at"], ["terms_accepted", "terms_accepted_at"], ["privacy_ack", "privacy_ack_at"], ["challenge25_ack", "challenge25_ack_at"]].map(([k, atKey]) => (
              <p key={k}>{legal[k] ? "✅" : "⬜"} {LEGAL_LABELS[k]} <span className="text-white/55 text-xs">{fmt(legal[atKey])}</span></p>
            ))}
            <p className="text-white/60 text-xs pt-2 border-t border-white/10 mt-2">Policy: terms {legal.policy_versions?.terms || "—"}, privacy {legal.policy_versions?.privacy || "—"}</p>
            <p className="text-white/60 text-xs flex items-center gap-1.5"><MailCheck className="h-3.5 w-3.5" /> Idempotency recorded: {r.has_idempotency_key ? "yes" : "no"} · Updated {fmt(r.updated_at)}</p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
